import React, { useState } from "react";

interface VehicleData {
  transaction_id: string;
  input_vehicle_registration_number: string;
  success: boolean;
  response_code: string;
  response_message: string;
  result: {
    extra_data: any;
    finance: {
      financer: string;
      vehicle_financed: string;
    };
    insurance: {
      company: string;
      policy_number: string;
      valid_upto: string;
      company_id: number;
      insurance_expired: string;
      month_year_remaining_for_insurance_exp: string;
    };
    national: {
      national_permit_expiry_date: string;
      national_permit_issued_by: string;
      national_permit_number: string;
    };
    user: {
      user_first_name: string;
      user_second_name: string;
      user_full_name: string;
      father_name: string;
      user_present_address: string;
      user_permanent_address: string;
      pincode: string;
    };
    rc_details: {
      rc_registration_number: string;
      rc_engine_number: string;
      rc_chassis_number: string;
      rc_registration_date: string;
      rc_registration_location: string;
      rc_blacklist_status: string;
      rc_rto_code: string;
      rc_state_code: string;
      rc_status: string;
      rc_status_as_on: string;
      rc_tax_upto: string;
      rc_source: string;
      rc_expiry_date: string;
      rc_fit_upto: string;
      rc_mapper_class: string;
      rc_commercial_status: string;
      rc_noc_details: string;
      rc_permit_expiry_date: string;
      rc_permit_issued_date: string;
      rc_permit_number: string;
      rc_permit_start_date: string;
      rc_permit_type: string;
      rc_pucc_expiry_date: string;
      rc_pucc_no: string;
    };
    vehicle_details: {
      vehicle_category: string;
      vehicle_class_description: string;
      vehicle_color: string;
      vehicle_cubic_capacity: string;
      vehicle_fuel_description: string;
      vehicle_gross_weight: string;
      vehicle_make_model: string;
      vehicle_make_id: string;
      vehicle_model_id: string;
      vehicle_maker_description: string;
      vehicle_manufactured_date: string;
      vehicle_number_of_cylinders: string;
      vehicle_seating_capacity: string;
      vehicle_sleeper_capacity: string;
      vehicle_stand_capacity: string;
      vehicle_unladen_weight: string;
      vehicle_wheelbase: string;
      vehicle_category_description: string;
      vehicle_fitness_expired: string;
      vehicle_age: string;
      city: string;
      state: string;
      vehicle_owner_number: string;
      vehicle_type: string;
      make: string;
      model: string;
      variant: string;
      exshowroomprice: string;
    };
  };
}
const App = () => {
  const [vehicleNumber, setVehicleNumber] = useState<string>("");
  const [vehicleData, setVehicleData] = useState<VehicleData | null>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [warning, setWarning] = useState<string>("");
  const [showFullResult, setShowFullResult] = useState<boolean>(false);
  const handleSubmit = async () => {
    console.log("Submit clicked!");
    setLoading(true); // Show the loader when API is being called
    setError("");
    setWarning("");
    try {
      const response = await fetch(
        "https://idv.wimwisure.com/vehicle-info",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            vehicle_registration_number: vehicleNumber,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }
      const data = await response.json();
      console.log("API response:", data);
      if (data.success) {
        setVehicleData(data);
      } else {
        setError("Failed to fetch data. Please try again.");
      }
    } catch (err: any) {
      console.error("API error:", err);
      setError("An error occurred while fetching the vehicle details.");
    } finally {
      setLoading(false); // Hide the loader when API call finishes
    }
  };
  const handleVehicleNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.toUpperCase(); // Convert to uppercase
    setVehicleNumber(value);
    // Validation for vehicle number: No spaces or special characters
    if (value.length >= 5 && /^[A-Za-z0-9]*$/.test(value)) {
      setWarning(""); // Clear warning if valid
    } else {
      setWarning(
        "Vehicle number must start with a letter and contain no spaces or special characters."
      );
    }
  };
 // Clean the exshowroom price to ensure it's a numeric value
const cleanExShowroomPrice = (price: string): number => {
  // Remove currency symbol and any non-numeric characters except for the decimal point
  let cleanedPrice = price.replace(/[^\d.]/g, ""); // Keep only digits and decimal points
  // Check if the value contains 'Lakh' or 'Cr' and adjust accordingly
  if (price.includes("Lakh")) {
    cleanedPrice = (parseFloat(cleanedPrice) * 100000).toString(); // Convert Lakh to numeric value
  } else if (price.includes("Cr")) {
    cleanedPrice = (parseFloat(cleanedPrice) * 10000000).toString(); // Convert Cr to numeric value
  } else {
    // If it's a number already, just return it
    cleanedPrice = parseFloat(cleanedPrice).toString();
  }
  // Convert to number before returning
  return parseFloat(cleanedPrice);
};
  // Calculate Vehicle Age based on RC registration date
  const calculateVehicleAge = (rcRegistrationDate: string): number => {
    const registrationDate = new Date(rcRegistrationDate);
    const currentDate = new Date();
    const ageInMilliseconds =
      currentDate.getTime() - registrationDate.getTime();
    const ageInYears = ageInMilliseconds / (1000 * 3600 * 24 * 365.25); // Account for leap years
    return ageInYears;
  };
  // Depreciation calculation
  const calculateDepreciation = (
    exShowroomPrice: number,
    vehicleAgeInYears: number
  ): number => {
    let depreciationRate = 0;
    if (vehicleAgeInYears <= 0.5) {
      depreciationRate = 5; // 5% for <= 6 months
    } else if (vehicleAgeInYears <= 1) {
      depreciationRate = 15; // 15% for 6 months - 1 year
    } else if (vehicleAgeInYears <= 2) {
      depreciationRate = 20; // 20% for 1 - 2 years
    } else if (vehicleAgeInYears <= 3) {
      depreciationRate = 30; // 30% for 2 - 3 years
    } else if (vehicleAgeInYears <= 4) {
      depreciationRate = 40; // 40% for 3 - 4 years
    } else if (vehicleAgeInYears <= 5) {
      depreciationRate = 50; // 50% for 4 - 5 years
    } else {
      depreciationRate = 50; // 50% for > 5 years
    }
    return depreciationRate;
  };
  // Calculate IDV
  const calculateIDV = (
    exShowroomPrice: string,
    rcRegistrationDate: string
  ): string => {
    const exShowroomPriceValue = cleanExShowroomPrice(exShowroomPrice); // Cleaned Exshowroom price
    const vehicleAgeInYears = calculateVehicleAge(rcRegistrationDate);
    const depreciationRate = calculateDepreciation(
      exShowroomPriceValue,
      vehicleAgeInYears
    );
    const idvValue = exShowroomPriceValue * (100 - depreciationRate) / 100;
    return `₹ ${idvValue.toLocaleString()}`;
  };
  const isFormValid =
    vehicleNumber.length >= 5 && /^[A-Za-z0-9]*$/.test(vehicleNumber);
  const handleShowFullResult = () => {
    setShowFullResult(true);
  };
  const handleCloseDialog = () => {
    setShowFullResult(false);
  };
  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f9f9f9",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <img
          src="https://survezy.s3.ap-south-1.amazonaws.com/logos/wimwisure.png"
          alt="WIMWIsure Logo"
          style={{ maxWidth: "200px", height: "auto" }}
        />
      </div>
      <h2
        style={{
          textAlign: "center",
          color: "black",
          padding: "10px",
          borderRadius: "5px",
        }}
      >
        VAHAAN & IDV CALCULATOR
      </h2>
      <div
        style={{
          maxWidth: "450px",
          margin: "20px auto",
          padding: "15px",
          border: "1px solid #007bff",
          borderRadius: "8px",
          backgroundColor: "#ffffff",
        }}
      >
        <input
          type="text"
          placeholder="ENTER VEHICLE NUMBER"
          value={vehicleNumber}
          onChange={handleVehicleNumberChange}
          style={{
            width: "95%",
            padding: "12px",
            marginBottom: "15px",
            borderRadius: "5px",
            border: "1px solid #4caf50",
            fontSize: "16px",
          }}
        />
        {warning && (
          <div style={{ color: "red", marginBottom: "15px" }}>{warning}</div>
        )}
        <button
          onClick={handleSubmit}
          disabled={loading || !isFormValid}
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "5px",
            fontSize: "16px",
            cursor: "pointer",
          }}
        >
          {loading ? "LOADING..." : "SUBMIT"}
        </button>
      </div>
      {vehicleData && (
        <div
          style={{
            marginTop: "30px",
            padding: "25px",
            border: "1px solid #007bff",
            borderRadius: "8px",
            backgroundColor: "#ffffff",
          }}
        >
          {/* Vehicle Details */}
          <h3
            style={{
              textAlign: "center",
              backgroundColor: "#4caf50",
              color: "white",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            VEHICLE DETAILS
          </h3>
          <div style={{ marginBottom: "20px" }}>
            <p>
              <strong>OWNER NAME:</strong>{" "}
              {vehicleData.result.user.user_full_name.toUpperCase()}
            </p>
            <p>
              <strong>MAKE:</strong> {vehicleData.result.extra_data.make.toUpperCase()}
            </p>
            <p>
              <strong>MODEL:</strong> {vehicleData.result.extra_data.model.toUpperCase()}
            </p>
            <p>
              <strong>VARIANT:</strong> {vehicleData.result.extra_data.variant.toUpperCase()}
            </p>
            <p>
              <strong>VEHICLE CATEGORY:</strong>{" "}
              {vehicleData.result.vehicle_details.vehicle_category.toUpperCase()}
            </p>
            <p>
              <strong>COLOR:</strong>{" "}
              {vehicleData.result.vehicle_details.vehicle_color.toUpperCase()}
            </p>
            <p>
              <strong>FUEL TYPE:</strong>{" "}
              {vehicleData.result.vehicle_details.vehicle_fuel_description.toUpperCase()}
            </p>
            <p>
              <strong>SEATING CAPACITY:</strong>{" "}
              {vehicleData.result.vehicle_details.vehicle_seating_capacity.toUpperCase()}
            </p>
          </div>
          {/* RC Details */}
          <h3
            style={{
              textAlign: "center",
              backgroundColor: "#ffa500",
              color: "white",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            RC DETAILS
          </h3>
          <div style={{ marginBottom: "20px" }}>
            <p>
              <strong>ENGINE NUMBER:</strong>{" "}
              {vehicleData.result.rc_details.rc_engine_number.toUpperCase()}
            </p>
            <p>
              <strong>CHASSIS NUMBER:</strong>{" "}
              {vehicleData.result.rc_details.rc_chassis_number.toUpperCase()}
            </p>
            <p>
              <strong>REGISTRATION DATE:</strong>{" "}
              {vehicleData.result.rc_details.rc_registration_date.toUpperCase()}
            </p>
            <p>
              <strong>REGISTRATION LOCATION:</strong>{" "}
              {vehicleData.result.rc_details.rc_registration_location.toUpperCase()}
            </p>
            <p>
              <strong>RTO CODE:</strong>{" "}
              {vehicleData.result.rc_details.rc_rto_code.toUpperCase()}
            </p>
          </div>
          {/* Insurance Details */}
          <h3
            style={{
              textAlign: "center",
              backgroundColor: "#4caf50",
              color: "white",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            INSURANCE DETAILS
          </h3>
          <div style={{ marginBottom: "20px" }}>
            <p>
              <strong>INSURANCE COMPANY:</strong>{" "}
              {vehicleData.result.insurance.company.toUpperCase()}
            </p>
            <p>
              <strong>POLICY NUMBER:</strong>{" "}
              {vehicleData.result.insurance.policy_number.toUpperCase()}
            </p>
            <p>
              <strong>VALID UNTIL:</strong>{" "}
              {vehicleData.result.insurance.valid_upto.toUpperCase()}
            </p>
          </div>
{/* Vehicle IDV Section */}
<h3
  style={{
    textAlign: "center",
    backgroundColor: "#ffa500",
    color: "white",
    padding: "10px",
    borderRadius: "5px",
  }}
>
  VEHICLE IDV
</h3>
<div style={{ marginBottom: "20px" }}>
  <p>
    <strong>VEHICLE EX-SHOWROOM PRICE:</strong>{" "}
    {" "}
    {vehicleData.result.extra_data.price_image_colors.exshowroomprice}
  </p>
  <p>
    <strong>VEHICLE AGE:</strong>{" "}
    {calculateVehicleAge(vehicleData.result.rc_details.rc_registration_date).toFixed(2)} years
  </p>
  <p>
    <strong>DEPRECIATION %:</strong>{" "}
    {vehicleData.result.extra_data.price_image_colors.exshowroomprice &&
    vehicleData.result.rc_details.rc_registration_date
      ? `${calculateDepreciation(
          parseFloat(vehicleData.result.extra_data.price_image_colors.exshowroomprice),
          calculateVehicleAge(vehicleData.result.rc_details.rc_registration_date)
        ).toFixed(2)}%`
      : "N/A"}
  </p>
  <p>
    <strong>VEHICLE IDV:</strong>{" "}
    {vehicleData.result.extra_data.price_image_colors.exshowroomprice &&
    vehicleData.result.rc_details.rc_registration_date
      ? calculateIDV(
          vehicleData.result.extra_data.price_image_colors.exshowroomprice,
          vehicleData.result.rc_details.rc_registration_date
        )
      : "N/A"}
  </p>
  {/* Conditional Note */}
  {calculateVehicleAge(vehicleData.result.rc_details.rc_registration_date) > 5 && (
    <p style={{ color: "red", fontStyle: "italic" }}>
      <strong>NOTE:</strong> IDV of vehicles beyond 5 years of age and of obsolete models of the vehicles (i.e. models which the manufacturers have discontinued to manufacture) is to be determined on the basis of an understanding between the insurer and the insured.
    </p>
  )}
</div>
          <button
            onClick={handleShowFullResult}
            style={{
              width: "100%",
              padding: "12px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            VIEW FULL RESULT
          </button>
        </div>
      )}
      {showFullResult && vehicleData && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            padding: "20px",
            overflowY: "scroll",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <h2 style={{ textAlign: "center", color: "#007bff" }}>
              FULL RESULT
            </h2>
            <pre style={{ whiteSpace: "pre-wrap" }}>
              {JSON.stringify(vehicleData, null, 2)}
            </pre>
            <button
              onClick={handleCloseDialog}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#ff0000",
                color: "white",
                border: "none",
                borderRadius: "5px",
                fontSize: "16px",
                cursor: "pointer",
              }}
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
